import { differenceInDays, isValid, parseISO, subDays } from "date-fns"
import { redirect } from "react-router-dom"
import { fetchCalendarItems, fetchLinkedinOrganizationPost, fetchSocialListeningPost, fetchSocialListeningPostMedias, fetchSocialListeningPostSourceURL, fetchSocialListeningPosts, fetchSocialListeningSummaries } from "../apis/admin"
import { fetchCalendarAccessRequests } from "../apis/calendar-sharing"
import { fetchAdminPost, fetchAdvocate, fetchAdvocateAnalytics, fetchAdvocates, fetchPostAnalytics, fetchPosts } from "../apis/post"
import { fetchSocialMediaShare } from "../apis/social-media-shares"
import { ADMIN_LOAD_POSTS_LIMIT, NOW, NOW_MINUS_1_WEEK, POST_SOURCE_FEED, POST_SOURCE_SOCIAL_LISTENING, POST_STATUS_DRAFT } from "../constants"
import { calendarRange, CALENDAR_VIEW, safeCalendarReturnTo } from "../helpers/calendar"
import { adminInspirationHubPagePath } from "../links"
import { getDateFromRelativeString, getDateString } from "../utils"

export function getTimePeriods(start, end) {
  let startDate, endDate

  if (start.startsWith(NOW)) {
    startDate = getDateFromRelativeString(start)
  } else {
    startDate = new Date(start)
  }

  if (end.startsWith(NOW)) {
    endDate = getDateFromRelativeString(end)
  } else {
    endDate = new Date(end)
  }

  const duration = differenceInDays(new Date(endDate), new Date(startDate))
  const previousStartDate = subDays(startDate, duration)
  const previousEndDate = startDate

  return {
    CurrentTimePeriodStart: getDateString(startDate),
    CurrentTimePeriodEnd: getDateString(endDate),
    PreviousTimePeriodStart: getDateString(previousStartDate),
    PreviousTimePeriodEnd: getDateString(previousEndDate),
  }
}

function getStartAndEndFromUrl(url) {
  let start = url.searchParams.get('start')
  let end = url.searchParams.get('end')
  if (!start) {
    start = NOW_MINUS_1_WEEK
  }
  if (!end) {
    end = NOW
  }
  return { start, end }
}

export function loadAdminAnalyticsPageData({ request }) {
  const url = new URL(request.url)
  const { start, end } = getStartAndEndFromUrl(url)
  const campaignIDs = url.searchParams.getAll('campaignIDs').map(id => parseInt(id)).filter(id => Number.isInteger(id))
  return { start, end, campaignIDs }
}

export async function loadAdminPostAnalyticsData({ request, params }) {
  const url = new URL(request.url)
  const { start, end } = getStartAndEndFromUrl(url)
  const { CurrentTimePeriodStart, CurrentTimePeriodEnd } = getTimePeriods(start, end)
  const orgID = url.searchParams.get('orgID')

  const postID = params.postID
  if (!postID) {
    throw new Error("postID is required")
  }

  try {
    const [postData, postAnalyticsData] = await Promise.all([
      fetchAdminPost({ orgID, postID, }),
      fetchPostAnalytics({ orgID, postID, TimePeriodStart: CurrentTimePeriodStart, TimePeriodEnd: CurrentTimePeriodEnd }),
    ])
    const { post, error } = postData
    const { analytics } = postAnalyticsData
    if (error) {
      throw new Error(error)
    }
    return { post, postAnalytics: analytics, start, end }
  } catch (e) {
    console.error("Error while loading post analytics data", e)
    throw e
  }
}

export async function loadAdminAdvocateAnalyticsData({ request, params }) {
  const url = new URL(request.url)
  const { start, end } = getStartAndEndFromUrl(url)
  const { CurrentTimePeriodStart, CurrentTimePeriodEnd } = getTimePeriods(start, end)
  const orgID = url.searchParams.get('orgID')

  const advocateID = params.advocateID
  if (!advocateID) {
    throw new Error("advocateID is required")
  }

  const queryParams = new URLSearchParams()
  if (orgID) {
    queryParams.append("orgID", orgID)
  }
  try {
    const [postData, postAnalyticsData] = await Promise.all([
      fetchAdvocate({ orgID, advocateID }),
      fetchAdvocateAnalytics({ orgID, advocateID, TimePeriodStart: CurrentTimePeriodStart, TimePeriodEnd: CurrentTimePeriodEnd }),
    ])
    const { advocate, error } = postData
    const { analytics } = postAnalyticsData
    if (error) {
      throw new Error(error)
    }
    return { advocate, advocateAnalytics: analytics, start, end }
  } catch (e) {
    console.error("Error while loading post analytics data", e)
    throw e
  }
}

export async function loadSocialMediaShareAnalyticsDrawerData({ params, request }) {
  const url = new URL(request.url)
  const orgID = url.searchParams.get('orgID')

  const { postID, socialMediaShareID } = params
  if (!postID || !socialMediaShareID) {
    throw new Error("postID and socialMediaShareID are required")
  }

  try {
    const [postData, socialMediaShareData] = await Promise.all([fetchAdminPost({ postID, orgID }), fetchSocialMediaShare({ socialMediaShareID, orgID })])
    const { post, error: postError } = postData
    const { socialMediaShare, error: socialMediaShareError } = socialMediaShareData
    if (postError) {
      throw new Error(postError)
    }
    if (socialMediaShareError) {
      throw new Error(socialMediaShareError)
    }
    return { post: post, socialMediaShare: socialMediaShare }
  } catch (e) {
    console.error("Error while loading social media share analytics data", e)
    throw e
  }
}

export async function loadAdminUsersPageData({ request }) {
  const url = new URL(request.url)
  const orgID = url.searchParams.get('orgID')
  const limit = url.searchParams.get('limit') || '20'
  const offset = url.searchParams.get('offset') || '0'

  const queryParams = new URLSearchParams({
    limit,
    offset
  })
  if (orgID) {
    queryParams.append("orgID", orgID)
  }
  try {
    const res = await fetch(`/api/admin/users?${queryParams.toString()}`)
    const { Data } = await res.json()
    return { users: Data, limit, offset }
  } catch (e) {
    console.error("Error while loading users data", e)
    throw e
  }
}

export async function loadAdminSavePostData({ params, request }) {
  const postID = params.postID

  const url = new URL(request.url)
  const orgID = url.searchParams.get('orgID')
  const companyPostID = url.searchParams.get('companyPostID')
  const type = url.searchParams.get('type')
  const socialListeningPostID = url.searchParams.get('socialListeningPostID')
  // a variation for the share target, scheduled at scheduledAt, is added to the post when creating it from the calendar
  const targetType = url.searchParams.get('targetType')
  const targetID = Number(url.searchParams.get('targetID'))
  const prefill = targetType && targetID ? { targetType, targetID, scheduledAt: url.searchParams.get('scheduledAt') } : null
  const returnTo = safeCalendarReturnTo(url.searchParams.get('returnTo'))

  const promises = [
    Promise.resolve({}),
    Promise.resolve({}),
    fetchAdvocates({ orgID, includeSocialAccount: true }),
    fetchSocialListeningPost({ orgID, postID: socialListeningPostID }),
    fetchSocialListeningPostSourceURL({ orgID, postID: socialListeningPostID }),
    fetchSocialListeningPostMedias({ orgID, postID: socialListeningPostID }),
  ]
  if (postID) {
    promises[0] = fetchAdminPost({ postID, orgID })
  }

  if (companyPostID) {
    promises[1] = fetchLinkedinOrganizationPost({ companyPostID, orgID })
  }

  try {
    const [postData, companyPostData, advocatesData, socialListeningPostData, socialListeningSourceURLData, socialListeningMediasData] = await Promise.all(promises)
    const { post, error: postError } = postData
    const { post: companyPost, error: companyPostError } = companyPostData
    const { advocates, error: advocatesError } = advocatesData
    const { post: socialListeningPost, error: socialListeningPostError } = socialListeningPostData
    // The LinkedIn post url is used as the source when the link in the post can't be resolved, so the error is not thrown
    const { sourceURL: socialListeningSourceURL, error: socialListeningSourceURLError } = socialListeningSourceURLData
    if (socialListeningSourceURLError) {
      console.error("Error while resolving the link in the LinkedIn post", socialListeningSourceURLError)
    }
    // The post can still be created without the media of the LinkedIn post, so the error is not thrown
    const { medias: socialListeningMedias, error: socialListeningMediasError } = socialListeningMediasData
    if (socialListeningMediasError) {
      console.error("Error while copying the media of the LinkedIn post", socialListeningMediasError)
    }
    if (postError) {
      throw new Error(postError)
    }
    if (companyPostError) {
      throw new Error(companyPostError)
    }
    if (advocatesError) {
      throw new Error(advocatesError)
    }
    if (socialListeningPostError) {
      throw new Error(socialListeningPostError)
    }

    return { advocates, post, companyPost, type, socialListeningPost, socialListeningSourceURL, socialListeningMedias, prefill, returnTo }
  } catch (e) {
    console.error("Error while loading post data", e)
    throw e
  }
}

export async function loadAdminInspirationHubPageData({ request }) {
  const url = new URL(request.url)
  const orgID = url.searchParams.get('orgID')
  const source = url.searchParams.get('source') || POST_SOURCE_FEED
  const limit = url.searchParams.get('limit') || ADMIN_LOAD_POSTS_LIMIT
  const offset = url.searchParams.get('offset') || '0'

  try {
    if (source === POST_SOURCE_SOCIAL_LISTENING) {
      const { posts, error, errorCode } = await fetchSocialListeningPosts({ orgID, limit, offset })
      // Social listening is not enabled for the organization
      if (errorCode === 422) {
        return redirect(adminInspirationHubPagePath(orgID, POST_SOURCE_FEED))
      }
      if (error) {
        throw new Error(error)
      }
      const { summaries, error: summariesError } = await fetchSocialListeningSummaries({ orgID })
      if (summariesError) {
        throw new Error(summariesError)
      }
      return { posts, summaries, source }
    }
    else if (source === POST_SOURCE_FEED) {
      const { posts, error } = await fetchPosts({
        userType: "admin",
        status: POST_STATUS_DRAFT,
        source,
        orgID,
        limit,
        offset
      })
      if (error) {
        throw new Error(error)
      }
      return { posts, source }
    } else {
      throw new Error("Invalid source")
    }
  } catch (e) {
    console.error("Error while loading inspiration hub data", e)
    throw e
  }
}

export async function loadAdminPostPageData({ params, request }) {
  const postID = params.postID
  if (!postID) {
    throw new Error("postID not found")
  }

  const url = new URL(request.url)
  const orgID = url.searchParams.get('orgID')

  try {
    const { post, error } = await fetchAdminPost({ postID, orgID })
    if (error) {
      throw new Error(error)
    }
    return { post }
  } catch (e) {
    console.error("Error while loading post data", e)
    throw e
  }
}

export async function loadAdminCalendarData({ request }) {
  const url = new URL(request.url)
  const orgID = url.searchParams.get('orgID')
  const view = url.searchParams.get('view') === CALENDAR_VIEW.WEEK ? CALENDAR_VIEW.WEEK : CALENDAR_VIEW.MONTH
  const parsedDate = parseISO(url.searchParams.get('date') || "")
  const date = isValid(parsedDate) ? parsedDate : new Date()
  const { from, to } = calendarRange(view, date)

  const [{ items, error: itemsError }, { advocates, error: advocatesError }, { requests, error: requestsError }] = await Promise.all([
    fetchCalendarItems({ orgID, from, to }),
    fetchAdvocates({ orgID, includeSocialAccount: true }),
    fetchCalendarAccessRequests({ orgID }),
  ])
  if (itemsError) {
    throw new Error(itemsError)
  }
  if (advocatesError) {
    throw new Error(advocatesError)
  }
  if (requestsError) {
    throw new Error(requestsError)
  }
  return { items, advocates, requests, view, date }
}
